import { insertDraftStreamContent } from "../../../lib/draftInsertion";
import { resolveRetiredDraftMode } from "../../../../../src/shared/retiredDirectWriteModels.js";
import { resolveLegacyVoiceProseMode } from "../../../../../src/shared/legacyVoiceRetirement.js";
import React, { useState, useCallback, useEffect, useRef, forwardRef } from "react";
import Utils, { countWords, parseCommands } from "../../../Utils";
import { StyledContainer } from "./styles";
import { useStoryBibleStore } from "../../../stores/storyBibleStore";
import CopyableTextarea from "../../StoryBible/CopyableTextarea";
import { FUNCTION_NAMES, INPUT_LIMITS } from "../../StoryBible";
import { convertQuotes } from "../../../lib/convertQuotes";
import {
  clearPendingStreamChunkBoundary,
  splitStreamChunkLines,
} from "../Utils";
import { MarkdownStreamer } from "../markdownStreamer";
import Header from "../../StoryBible/Header";
import ParsedBeatsBox from "../../StoryBible/ParsedBeatsBox";
import classNames from "classnames";
import SVG from "react-inlinesvg";
import { useProjectStore } from "../../../stores/projectStore";
import { useSudowriteStore } from "../../../stores/sudowriteStore";
import { Link } from "react-router-dom";
import { sendInvocationEvent } from "../../../Utils";
import InlineSVG from "react-inlinesvg";
import IconButton from "../../IconButton";
import _ from "lodash";
import Auth from "../../../Auth";
import { getDraftCTAUpdate } from "../newDocumentCTALifecycle";
import MuseGuidelines from "./MuseGuidelines";
import Textarea from "../../StoryBible/Textarea";
import { useToast } from "../../ToastWrapper";
import ScenesUpdate from "./ScenesUpdate";

const POSITION_STATES = {
  INITIAL: "initial",
  STREAMING: "streaming",
  SCROLLED_UP: "scrolled-up",
  SCROLLED_DOWN: "scrolled-down",
};

// used to calc offset for sticky position of prose generation buttons
const DEFAULT_BOTTOM_BAR_HEIGHT = 50;
const BOTTOM_BAR_WHITESPACE = 75;

function ProseGenerator(props) {
  const { editor, editorRef, editorEndRef } = props;
  const sceneToWrite = useStoryBibleStore((state) => state.sceneToWrite);
  const closeHelp = useStoryBibleStore((state) => state.closeHelp);
  const scenes = useStoryBibleStore((state) => state.scenes);
  const setScenes = useStoryBibleStore((state) => state.setScenes);
  const recentInvocations = useStoryBibleStore((state) => state.recentInvocations);
  const setRecentInvocations = useStoryBibleStore((state) => state.setRecentInvocations);
  const isStreaming = useStoryBibleStore((state) => state.isStreaming);
  const outOfQuota = useStoryBibleStore((state) => state.outOfQuota);
  const streamRequestRef = useStoryBibleStore((state) => state.streamRequestRef);
  const sceneId = useStoryBibleStore((state) => state.sceneId);
  const requesting = useStoryBibleStore((state) => state.requesting);
  const abortStream = useStoryBibleStore((state) => state.abortStream);
  const abortController = useStoryBibleStore((state) => state.abortController);
  const streamTimedOut = useStoryBibleStore((state) => state.streamTimedOut);
  const setHistory = useStoryBibleStore((state) => state.setHistory);
  const storyEngineIdRef = useStoryBibleStore((state) => state.storyEngineIdRef);
  const proseGeneratorActive = useStoryBibleStore((state) => state.proseGeneratorActive);
  const setProseGeneratorActive = useStoryBibleStore((state) => state.setProseGeneratorActive);
  const outlineObject = useStoryBibleStore((state) => state.outlineObject);
  const elementRefs = useStoryBibleStore((state) => state.elementRefs);
  const previousSceneLabel = useStoryBibleStore((state) => state.previousSceneLabel);
  const isActive = useStoryBibleStore((state) => state.isActive);
  const PROSE_MODES = useStoryBibleStore((state) => state.PROSE_MODES);

  const subscriptionJSON = useSudowriteStore((state) => state.subscriptionJSON);

  const automaticScroll = useProjectStore((state) => state.automaticScroll);
  const setUserScrolled = useProjectStore((state) => state.setUserScrolled);
  const resetScrollCount = useProjectStore((state) => state.resetScrollCount);
  const setFeedbackInvocation = useProjectStore((state) => state.setFeedbackInvocation);
  const project = useProjectStore((state) => state.project);
  const setFeedbackOpen = useProjectStore((state) => state.setFeedbackOpen);
  const setShowNewDocumentCTAs = useProjectStore((state) => state.setShowNewDocumentCTAs);
  const getDocumentScenes = useStoryBibleStore((state) => state.getDocumentScenes);
  const setDocumentScenes = useStoryBibleStore((state) => state.setDocumentScenes);
  const documentScenes = getDocumentScenes(props.documentId);

  const [lastCommandIndex, setLastCommandIndex] = useState(0);
  const setStoreProseMode = useStoryBibleStore((state) => state.setProseMode);
  const streamRequest = streamRequestRef.current;
  const { setToast } = useToast();

  const handleTextareaInput = (e, key) => {
    const length = countWords(e.target.value);

    if (length >= INPUT_LIMITS[key]) {
      setToast(`The maximum length for this field is ${INPUT_LIMITS[key]} words. Your text has been truncated to fit.`);
    }
  };

  const commandsOnInput = useCallback((input) => handleTextareaInput(input, FUNCTION_NAMES.COMMANDS), []);
  const scene = scenes?.[sceneId];
  // Global Draft defaults can come from the chapter generator, whose benchmark
  // experience is not supported by this legacy generator.
  const legacyVoiceModels = useSudowriteStore((state) => state.customModels);
  const selectedProseMode = scene?.proseMode && PROSE_MODES[resolveRetiredDraftMode(resolveLegacyVoiceProseMode(scene.proseMode, legacyVoiceModels))];
  const proseMode = selectedProseMode?.benchmarkMode ? PROSE_MODES.EXCELLENT : selectedProseMode || PROSE_MODES.EXCELLENT;
  const sceneSummary = sceneToWrite?.summary;
  const paused = scene?.lastCommandIndex !== undefined;
  const [userStyleExamples, setUserStyleExamples] = useState(() => {
    const savedExamples = localStorage.getItem("userStyleExamples");
    return savedExamples ? JSON.parse(savedExamples) : "";
  });
  const [documentScenesString, setDocumentScenesString] = useState("");

  useEffect(() => {
    setShowNewDocumentCTAs(getDraftCTAUpdate(proseGeneratorActive, () => editor?.getText?.()));
  }, [proseGeneratorActive]);

  useEffect(() => {
    localStorage.setItem("userStyleExamples", JSON.stringify(userStyleExamples));
  }, [userStyleExamples]);

  useEffect(() => {
    if (scene) {
      setLastCommandIndex(scene?.lastCommandIndex);
    }
  }, [scene?.lastCommandIndex]);

  // Scroll to top when prose generator is active
  useEffect(() => {
    if (proseGeneratorActive) {
      document.querySelector(".editor-container-container").scrollIntoView({ behavior: "smooth" });
    }
  }, [proseGeneratorActive]);

  useEffect(() => {
    if (!project.useScenes) {
      return;
    }
    const load = async () => {
      if (!documentScenes && props.documentId) {
        const scenes = await Auth.fetchWithIdToken(`/api/scenes/`, {
          documentId: props.documentId,
        });
        setDocumentScenes(props.documentId, scenes);
      }
    };
    load();
  }, [props.documentId, documentScenes, setDocumentScenes, project.useScenes]);

  const [estimating, setEstimating] = useState(false);
  const proseGeneratorRef = useRef(null);
  const bottomBarInfoRef = useRef(null);
  const markdownStreamerRef = useRef(null);
  const draftInsertionStateRef = useRef({ prepared: false });
  const placeholderCardRef = useRef(null);
  const [height, setHeight] = useState(250);

  // need this to distinguish beats from prose for info displayed in bottom bar
  const [isStreamingBeats, setIsStreamingBeats] = useState(false);

  const [estimatedCredits, setEstimatedCredits] = useState(null);

  useEffect(() => {
    if (!proseGeneratorRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      const bottomBarHeight = (bottomBarInfoRef?.current?.offsetHeight || DEFAULT_BOTTOM_BAR_HEIGHT) + BOTTOM_BAR_WHITESPACE;
      const newHeight = proseGeneratorRef?.current?.offsetHeight - bottomBarHeight;
      setHeight(typeof newHeight === "number" ? newHeight : 250);
    });

    resizeObserver.observe(proseGeneratorRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [setHeight]);

  useEffect(() => {
    setStoreProseMode(proseMode);
  }, [proseMode]);

  const insertDraftContent = (content) => insertDraftStreamContent(editor, content, draftInsertionStateRef.current);

  const updateEditor = (rawChunkText) => {
    const chunkText = convertQuotes(rawChunkText, editor);
    if (chunkText) {
      editor.commands.focus("end");
      const streamer = markdownStreamerRef.current;
      const lines = splitStreamChunkLines(chunkText, editor);

      if (chunkText.indexOf("\n") === -1 && lines.length === 1) {
        // no newline present, so we just insert
        const html = streamer ? streamer.write(lines[0]) : lines[0];
        if (html) {
          insertDraftContent(html);
        }

        automaticScroll(editorEndRef);
      } else {
        for (let i = 0; i < lines.length; i++) {
          if (lines[i].length > 0) {
            const html = streamer ? streamer.write(lines[i]) : lines[i];
            if (html) {
              insertDraftContent(html);
            }
          }

          if (i < lines.length - 1) {
            // Flush pending markdown before paragraph breaks
            if (streamer) {
              const pendingHtml = streamer.flush();
              if (pendingHtml) {
                insertDraftContent(pendingHtml);
              }
            }
            // Plain paragraph boundary — no spacer empty paragraph; visual
            // separation comes from the Paragraph Spacing setting.
            insertDraftContent([
              {
                type: "paragraph",
                content: [],
              },
            ]);

            editor.commands.setInserted();
          }
        }
      }
    }
  };

  const estimateProseCredits = useCallback(
    _.debounce(async (proseMode, scene, sceneSummary, isEstimating) => {
      if (isEstimating) {
        return;
      }

      if (scene?.commands) {
        setEstimating(true);
        try {
          const res = await Auth.fetchWithIdToken(`/api/estimator/storyEngine/write/${proseMode.value}`, {
            commands: scene?.commands,
            sceneSummary,
            engine: proseMode.engine,
            maxTokens: proseMode.maxTokens,
            startCommandIndex: scene?.lastCommandIndex,
            projectId: props.projectId,
            sceneId,
          });
          setEstimatedCredits(res.credits);
        } catch (e) {
          // noop
        } finally {
          setEstimating(false);
        }
      } else {
        setEstimatedCredits(null);
      }
    }, 5000),
    []
  );

  useEffect(() => {
    if (subscriptionJSON.usesLedgers) {
      estimateProseCredits(proseMode, scene, sceneSummary, estimating);
    }
  }, [proseMode, scene, sceneSummary]);

  const generateBeats = () => {
    setIsStreamingBeats(true);
    setScenes((draft) => {
      draft[sceneId].commands = "";
    });
    const url = project?.useScenes ? "/api/ai/storyEngine/generate-scenes" : "/api/ai/storyEngine/commands";
    streamRequest(
      // url
      url,
      // additionalData
      {
        sceneSummary, // Pass the sceneSummary to the streamRequest
        sceneId,
      },
      // update
      (text) => {
        if (text) {
          if (project.useScenes) {
            setDocumentScenesString(text);
          }
          setScenes((draft) => {
            draft[sceneId].commands += text;
          });
        }
      },
      // abortSignal
      abortController.current.signal,
      // requestType
      `commands-${sceneId}`
    );
    return true;
  };

  // Temporary experimental function for My Voice
  const generateSceneDirections = () => {
    setIsStreamingBeats(true);
    setScenes((draft) => {
      draft[sceneId].commands = "";
    });
    streamRequest(
      // url
      "/api/ai/storyEngine/scene-directions",
      // additionalData
      {
        sceneSummary, // Pass the sceneSummary to the streamRequest
        sceneId,
      },
      // update
      (text) => {
        if (text) {
          setScenes((draft) => {
            draft[sceneId].commands += text;
          });
        }
      },
      // abortSignal
      abortController.current.signal,
      // requestType
      `scene-directions-${sceneId}`
    );
    return true;
  };

  /**
   * This sends invocation events associated with a prose generation (continue or not)
   * @param {String} sceneId
   */
  const sendInvocationEventsForProseGenerate = (sceneId) => {
    const commandInvocationId = recentInvocations.commands[sceneId]; // gets the most recent command invocation for this prose generate

    if (commandInvocationId) {
      // record an event that tracks when a prose generation occurs for an "untouched" command invocation
      sendInvocationEvent(commandInvocationId, "prose-generated-from-commands");
    }
  };

  const generateProse = async () => {
    if (!scene.commands || scene.commands.trim().length === 0) {
      setToast("Please write beats first");
      return false;
    }

    clearPendingStreamChunkBoundary(editor);
    draftInsertionStateRef.current = { prepared: false };
    sendInvocationEventsForProseGenerate(sceneId);

    editor.setOptions({ editable: false });
    markdownStreamerRef.current = new MarkdownStreamer();

    const commands = project.useScenes ? documentScenes : scene.commands;
    const scenesEngine = project.useScenes && proseMode.scenesEngine ? proseMode.scenesEngine : null;
    const url = scenesEngine ? "/api/ai/storyEngine/write/chapterGenerator" : `/api/ai/storyEngine/write/${proseMode.value}`;

    // SW-9212: add a placeholder history card up front so it appears in the
    // right rail immediately and streams prose into itself.
    const placeholderCard = props.addDraftPlaceholderCard?.({ prompt: commands });
    placeholderCardRef.current = placeholderCard;

    streamRequest(
      url,
      {
        commands,
        sceneSummary,
        startCommandIndex: scene.restartFromIndex || 0,
        text: editor.getText(),
        isErrorRecovery: streamTimedOut,
        engine: proseMode.engine,
        maxTokens: proseMode.maxTokens,
        temperature: scene.creativityLevel,
        isContinue: scene.isContinue,
        version: proseMode.version,
        userStyleExamples: userStyleExamples,
        scenesEngine,
      },
      (chunkText) => {
        updateEditor(chunkText);
        props.appendDraftPlaceholderContent?.(placeholderCardRef.current, chunkText, { index: 0 });
      },
      abortController.current.signal,
      `sceneProse-${sceneId}`,
      // onChunk
      (chunk) => {
        if (chunk.commandIndex !== undefined) {
          // keep track of last command used for the scene
          setScenes((draft) => {
            draft[sceneId].lastCommandIndex = chunk.commandIndex;
            draft[sceneId].generationFinished = false;
          });
        }
        if (chunk.cardId) {
          props.linkDraftPlaceholderToCardId?.(placeholderCardRef.current, chunk.cardId);
        }
      },
      // onEnd
      () => {
        // Flush any remaining markdown tokens
        if (markdownStreamerRef.current) {
          const remainingHtml = markdownStreamerRef.current.flush();
          if (remainingHtml) {
            insertDraftContent(remainingHtml);
          }
        }
        editor.setOptions({ editable: true });
        // Detect if it's a My Voice model
        if (proseMode?.key?.startsWith("CUSTOM_")) {
          const { beats } = parseCommands(scene?.commands);
          setScenes((draft) => {
            const maxCommandIndex = beats.length - 1;
            // Increment the current scene index unless it's the last one, so that My Voice users can easily run the next stride
            if (draft[sceneId].lastCommandIndex < maxCommandIndex) {
              draft[sceneId].lastCommandIndex = draft[sceneId].lastCommandIndex + 1;
              draft[sceneId].restartFromIndex = draft[sceneId].lastCommandIndex;
            }
            draft[sceneId].generationFinished = true;
          });
        } else {
          setScenes((draft) => {
            delete draft[sceneId].lastCommandIndex;
            draft[sceneId].generationFinished = true;
            draft[sceneId].restartFromIndex = 0;
          });
        }
        props.finalizeDraftPlaceholder?.(placeholderCardRef.current);
        placeholderCardRef.current = null;
      },
      // onTimeout
      () => {
        if (markdownStreamerRef.current) {
          const remainingHtml = markdownStreamerRef.current.flush();
          if (remainingHtml) {
            insertDraftContent(remainingHtml);
          }
        }
        editor.setOptions({ editable: true });
        props.finalizeDraftPlaceholder?.(placeholderCardRef.current);
        placeholderCardRef.current = null;
      },
      // onStart
      () => {
        setUserScrolled(false);
        resetScrollCount();
      },
      // onError
      () => {
        if (markdownStreamerRef.current) {
          const remainingHtml = markdownStreamerRef.current.flush();
          if (remainingHtml) {
            insertDraftContent(remainingHtml);
          }
        }
        editor.setOptions({ editable: true });
        const placeholder = placeholderCardRef.current;
        if (placeholder) {
          const hasContent = Array.isArray(placeholder.data?.content) && placeholder.data.content.some((c) => typeof c === "string" && c.trim().length > 0);
          if (hasContent) {
            props.finalizeDraftPlaceholder?.(placeholder);
          } else {
            props.removeDraftPlaceholderCard?.(placeholder);
          }
          placeholderCardRef.current = null;
        }
      }
    );

    return true;
  };

  useEffect(() => {
    if (!isStreaming && !requesting) {
      setIsStreamingBeats(false);
    }
  }, [isStreaming]);

  if (!proseGeneratorActive) return null;

  const generateDisabled = !isActive || /*outlineObject?.sections.length === 0 ||*/ !sceneToWrite;
  const generateDisabledMessage = () => {
    switch (true) {
      case !isActive && outlineObject?.sections.length === 0:
        return "Turn on Story Bible and fill out your Outline to generate Beats";
      case !isActive && !!sceneToWrite:
        return "Turn on Story Bible to generate Beats";
      // case isActive && outlineObject?.sections.length === 0:
      //   return (
      //     <>
      //       Fill out your&nbsp;
      //       <a
      //         href="#"
      //         onClick={(e) => {
      //           e.preventDefault();
      //           elementRefs?.outline?.scrollIntoView({ behavior: "smooth" });
      //         }}
      //       >
      //         Outline
      //       </a>
      //       &nbsp;to generate beats based on a Chapter
      //     </>
      //   );
      case isActive && !sceneToWrite:
        return "Link a Chapter from your Outline to generate Beats";
      default:
        return;
    }
  };

  let proseGenerateDisabled = false;
  let proseGenerateDisabledMessage = null;

  if (!subscriptionJSON.hasAccessToStoryEngineProseGenerate) {
    proseGenerateDisabled = true;
    proseGenerateDisabledMessage = (
      <>
        This isn't available on your free trial. <Link to="/app/plans?ref=prose-gen">Subscribe to Sudowrite</Link>.
      </>
    );
  } else if (!scene?.commands?.trim()) {
    proseGenerateDisabled = true;
    proseGenerateDisabledMessage = "Add a numbered list of Beats to generate a chapter.";
  }

  return (
    <StyledContainer
      ref={proseGeneratorRef}
      className={classNames({ "prose-generator": true, [POSITION_STATES.SCROLLED_DOWN]: true })}
      style={{ top: `-${height}px` }}
    >
      <CopyableTextarea
        documentScenes={documentScenes}
        setDocumentScenes={_.debounce(async (newValue) => {
          const updated = await Auth.fetchWithIdToken(`/api/scenes/`, { method: "PUT", documentId: props.documentId, value: newValue });
          setDocumentScenes(props.documentId, updated);
        }, 3000)}
        generateLabel={project.useScenes ? "Generate Scenes" : "Generate Beats"}
        generateButtonType="beats"
        generateDisabled={generateDisabled}
        generateDisabledMessage={generateDisabledMessage()}
        isInChapter={true}
        help="This section affects: Prose"
        closeHelp={closeHelp}
        placeholder={`Write a numbered list of Beats. Beats are step by step instructions for the AI for how to write your chapter. Each Beat needs enough meat to write 200 words or so.

e.g. 1. Begin in Sarah’s home, where we see her drinking a cup of coffee and reading the paper. She hears a loud boom and rushes to the window where she sees an explosion in the distance. She panics and runs around the house looking for the rest of the family.`}
        generate={generateBeats}
        generateSceneDirections={generateSceneDirections}
        history={() =>
          setHistory({
            storyEngineId: storyEngineIdRef.current,
            key: "scene-commands",
            id: sceneId,
            title: project.useScenes ? "Scenes" : "Beats",
            onRestore: (contents) => {
              setScenes((draft) => {
                draft[sceneId].commands = contents;
              });
            },
          })
        }
        title={project.useScenes ? "Step 1: Scenes" : "Step 1: Beats"}
        requesting={requesting}
        value={scene?.commands}
        sceneId={sceneId}
        sceneKey={"commands"}
        setScenes={setScenes}
        onInput={commandsOnInput}
        maxLength={INPUT_LIMITS[FUNCTION_NAMES.COMMANDS]}
        type={FUNCTION_NAMES.COMMANDS}
        recentInvocations={recentInvocations}
        setRecentInvocations={setRecentInvocations}
        id={sceneId}
        setFeedbackOpen={setFeedbackOpen}
        setFeedbackInvocation={setFeedbackInvocation}
        abortStream={abortStream}
        outOfQuota={outOfQuota}
        loadAnimationDuration={30}
        isStreaming={isStreaming}
        proseMode={proseMode}
        preHeaderComponents={
          <MainHeader
            isActive={isActive}
            sceneToWrite={sceneToWrite}
            sceneSummary={sceneSummary}
            previousSceneLabel={previousSceneLabel}
            elementRefs={elementRefs}
            setProseGeneratorActive={setProseGeneratorActive}
          />
        }
        topComponents={proseMode?.key?.startsWith("MUSE05") || proseMode?.key?.startsWith("MUSE06") ? <MuseGuidelines /> : null}
        // Uses render props pattern here to configure bottomBar at runtime
        bottomBar={(props) => [
          <div style={{ width: "100%", position: "relative" }}>
            {(proseMode?.key?.startsWith("MUSE05") || proseMode?.key?.startsWith("MUSE06")) && (
              <div>
                <div style={{ fontWeight: 500, color: "var(--grey-0)", marginTop: 20 }}>Style Examples (optional)</div>
                <div style={{ color: "var(--grey-1)", lineHeight: 1.5 }}>Improve this prose mode's style by showing it examples of what you want.</div>
                <Textarea
                  maxWords={1000} // Assuming an average of 25 words per line
                  placeholder={`Show the AI up to 1000 words representative of your writing style. Unrelated samples should go on their own lines. For best results, use the same tense and POV as your story.

Examples:
Our fingers brushed, and I felt a spark – static from the dry air, but it jolted me nonetheless.
My hands were numb, but I felt a warmth in my chest, an uncomfortable heat that I recognized as guilt.`}
                  setValue={setUserStyleExamples}
                  value={userStyleExamples}
                  onChange={(e) => setUserStyleExamples(e.target.value)}
                  height="short"
                  hideWordCount={false}
                />
              </div>
            )}
          </div>,
          <div
            className="parsed-beats"
            key="parsed-beat"
          >
            <ParsedBeatsBox
              sceneCommands={scene?.commands || ""}
              proseMode={proseMode}
            />
          </div>,

          <BottomBarInfo
            ref={bottomBarInfoRef}
            key="estimate"
            scene={scene}
            paused={paused}
            isStreamingBeats={isStreamingBeats}
            estimatedCredits={estimatedCredits}
            lastCommandIndex={lastCommandIndex}
            setLastCommandIndex={setLastCommandIndex}
            proseMode={proseMode}
            header={
              <Header
                key="header"
                className={"textarea-header prose-actions"}
                title={null}
                textContent={""}
                requesting={requesting}
                generate={generateProse}
                abortStream={abortStream}
                lastInvocation={props.lastInvocation}
                setFeedbackOpen={setFeedbackOpen}
                setFeedbackInvocation={setFeedbackInvocation}
                outOfQuota={outOfQuota}
                loadAnimationDuration={30}
                isStreaming={isStreaming}
                setLastInvocation={props.setLastInvocation}
                pausable
                paused={paused}
                continueLabel={"Continue"}
                isInChapter={true}
                proseModes={PROSE_MODES}
                proseMode={proseMode}
                generateButtonType={"prose"}
                setScenes={setScenes}
                sceneId={sceneId}
                editor={editor}
                generateProse={generateProse}
                scene={scene}
                generateDisabled={proseGenerateDisabled}
                generateDisabledMessage={proseGenerateDisabledMessage}
                isFooter={true}
                setLastCommandIndex={setLastCommandIndex}
                editorRef={editorRef}
              />
            }
          />,
        ]}
        ariaLabel="Edit Beats Text"
        listFieldValueString={documentScenesString}
      />
    </StyledContainer>
  );
}

function SummaryBox({ sceneSummary = "", sceneToWrite }) {
  const [truncate, setTruncate] = useState(true);
  const truncateLength = 180;
  const truncatedSummary = sceneSummary.slice(0, truncateLength);
  return (
    <div className="summary-box">
      <span className="current-chapter">{sceneToWrite.label}: </span>
      <span
        tabIndex={0}
        aria-label={`Selected Scene Summary: ${sceneSummary}`}
      >
        {truncate ? truncatedSummary : Utils.nl2br(sceneSummary)}
        {sceneSummary.length > truncateLength && (
          <>
            <span>{truncate ? "... " : ""}</span>
            <span
              className="show-hide-summary"
              onClick={() => setTruncate(!truncate)}
            >
              {truncate ? "show more" : "  show less"}
            </span>
          </>
        )}
      </span>
    </div>
  );
}

function OutlineLinkMissingCTA({ chapter, elementRefs }) {
  return (
    <div className="outline-link-missing-cta">
      <div className="icon">
        <SVG src="/assets/story-bible/neutral-face.svg" />
      </div>
      <div className="text">
        Heads up - {chapter} is missing from your{" "}
        <a
          onClick={(e) => {
            e.preventDefault();
            elementRefs?.outline?.scrollIntoView({ behavior: "smooth" });
          }}
        >
          Outline
        </a>
        . Fill it out if you want to generate Beats.
      </div>
    </div>
  );
}

const BottomBarInfo = forwardRef(({ scene, paused, estimatedCredits, isStreamingBeats, header, proseMode }, ref) => {
  const APPX_WORDS_GENERATED_PER_BEAT = 200;
  const { beats } = parseCommands(scene?.commands || "");

  let estimatedWords;
  if (proseMode?.strides === false) {
    estimatedWords = 4000; // without strides, it always generates a whole chapter
  } else {
    estimatedWords = beats?.length * APPX_WORDS_GENERATED_PER_BEAT;
  }

  const isStreaming = useStoryBibleStore((state) => state.isStreaming);
  const columnWidths = useProjectStore((state) => state.columnWidths);

  const [smallInfo, setSmallInfo] = useState(false);

  useEffect(() => {
    if (columnWidths.center.current <= 535) {
      setSmallInfo(true);
    } else {
      setSmallInfo(false);
    }
  }, [columnWidths.center]);

  let info;
  if (scene?.generationFinished) {
    info = `Finished writing beats`;
  } else if (!isStreaming && !paused) {
    if (estimatedCredits) {
      info = (
        <>
          Estimated words: ~{estimatedWords} &middot; Estimated credits: ~{estimatedCredits}
        </>
      );
    } else {
      info = `Estimated words: ~${estimatedWords}`;
    }
  } else if (isStreaming && !isStreamingBeats) {
    if (proseMode?.key?.startsWith("CUSTOM_")) {
      info = `Writing Beats ${scene.lastCommandIndex + 1}`;
    } else if (proseMode?.strides === false) {
      info = `Writing Beats ${scene.lastCommandIndex + 1} to ${beats.length}`;
    } else if (proseMode?.key === "DEEP_SEEK_R1") {
      info = `Writing Beats ${scene.lastCommandIndex + 1} to ${scene.lastCommandIndex + 4} - This can take a few minutes before writing starts.`;
    } else {
      info = scene.lastCommandIndex + 1 < beats.length ? `Writing Beats ${scene.lastCommandIndex + 1} & ${scene.lastCommandIndex + 2} together` : `Writing Beat ${scene.lastCommandIndex + 1}`;
    }
  } else if (paused) {
    info = `Paused at the middle of Beat ${(scene.lastCommandIndex || 0) + 1}`;
  }

  return (
    <div
      className="bottom-bar-prose-generator"
      ref={ref}
    >
      <div className="inner">
        <div className="left">
          <div className="left-container">
            <div className="title">Step 2: Prose</div>
          </div>
          <div className={classNames("bottom-bar-info", { "info-hidden": !estimatedWords, "small-info": smallInfo })}>{info}</div>
        </div>
        {header}
      </div>
    </div>
  );
});

const MainHeader = ({ isActive, sceneToWrite, sceneSummary, previousSceneLabel, elementRefs, openHelp, closeHelp, setProseGeneratorActive }) => {
  return (
    <div className="main-header">
      <div className={classNames("inner", { "no-scene-summary": !sceneToWrite })}>
        <div className={classNames("left-container")}>
          <div className="title">
            <InlineSVG
              aria-hidden="true"
              src={"/assets/story-bible/generator.svg"}
            ></InlineSVG>
            Chapter Generator
          </div>

          {/* <div className="close"> */}
          <IconButton
            className="icon-button-close"
            onClick={() => setProseGeneratorActive(false)}
            src="/assets/close.svg"
            ariaLabel="Close Chapter Generator"
            hoverTipText="Hide"
            iconSize={32}
          />
          {/* </div> */}
        </div>
        <ScenesUpdate />
        {isActive &&
          (!!sceneToWrite ? (
            <SummaryBox
              sceneSummary={sceneSummary}
              sceneToWrite={sceneToWrite}
            />
          ) : (
            previousSceneLabel && (
              <OutlineLinkMissingCTA
                chapter={previousSceneLabel}
                elementRefs={elementRefs}
              />
            )
          ))}
      </div>
    </div>
  );
};

export default React.memo(ProseGenerator);
